import { useCallback, useEffect, useState } from "react";
import { Plus, Trash2, FlaskConical, Warehouse } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TechSelect } from "@/components/TechAssign";
import { api, errMsg } from "@/lib/api";

const KIND_LABEL = { lab: "Lab", storage: "Storage area" };

function RoomRow({ r, techs, onChanged }) {
  const [name, setName] = useState(r.name);
  const [aliases, setAliases] = useState((r.aliases || []).join(", "));
  const save = async (patch) => {
    try {
      const { data } = await api.put(`/rooms/${r.id}`, { name: r.name, kind: r.kind, notes: r.notes, technician_id: r.technician_id, aliases: r.aliases || [], ...patch });
      toast.success(data.merged?.length ? `Saved · merged ${data.merged.join(", ")} into ${data.name}` : "Saved");
      onChanged();
    } catch (e) {
      toast.error(errMsg(e));
      setName(r.name);
    }
  };
  const remove = async () => {
    const warn = r.items ? ` ${r.items} inventory item${r.items === 1 ? " still uses" : "s still use"} it as their location and will show as "not in list".` : "";
    if (!window.confirm(`Remove ${r.name}?${warn}`)) return;
    try {
      await api.delete(`/rooms/${r.id}`);
      toast.success(`${r.name} removed`);
      onChanged();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="flex flex-wrap items-center gap-2 py-2 border-b last:border-0" data-testid={`room-row-${r.id}`}>
      <Input value={name} onChange={(e) => setName(e.target.value)} onBlur={() => name.trim() && name !== r.name && save({ name })} className="h-9 w-56" data-testid={`room-name-${r.id}`} />
      <Select value={r.kind} onValueChange={(v) => save({ kind: v })}>
        <SelectTrigger className="h-9 w-36" data-testid={`room-kind-${r.id}`}><SelectValue /></SelectTrigger>
        <SelectContent><SelectItem value="lab">Lab</SelectItem><SelectItem value="storage">Storage area</SelectItem></SelectContent>
      </Select>
      {r.kind === "lab" && <TechSelect techs={techs} value={r.technician_id} onChange={(v) => save({ technician_id: v })} testId={`room-tech-${r.id}`} placeholder="No technician" />}
      <span className="text-xs text-muted-foreground">{r.items} item{r.items === 1 ? "" : "s"}{r.kind === "lab" && ` · ${r.lessons} lesson${r.lessons === 1 ? "" : "s"}`}</span>
      {r.from_import && <span className="rounded-full bg-amber-500/15 text-amber-700 dark:text-amber-300 px-2 py-0.5 text-[10px]" data-testid={`room-from-import-${r.id}`}>Added by timetable import</span>}
      <Button size="icon" variant="ghost" className="ml-auto h-8 w-8 text-destructive" onClick={remove} aria-label={`Remove ${r.name}`} data-testid={`room-delete-${r.id}`}><Trash2 className="h-4 w-4" /></Button>
      {r.kind === "lab" && (
        <Input value={aliases} onChange={(e) => setAliases(e.target.value)} placeholder="Other names used in timetables, e.g. Lab 1, SCI1" className="h-8 text-xs basis-full"
          onBlur={() => aliases !== (r.aliases || []).join(", ") && save({ aliases: aliases.split(",").map((a) => a.trim()).filter(Boolean) })} data-testid={`room-aliases-${r.id}`} />
      )}
    </div>
  );
}

function AddRoom({ onAdded }) {
  const [f, setF] = useState({ name: "", kind: "lab" });
  const add = async () => {
    try {
      const { data } = await api.post("/rooms", f);
      toast.success(data.merged?.length ? `${f.name} added · took over timetable lessons from ${data.merged.join(", ")}` : `${f.name} added`);
      setF({ name: "", kind: f.kind });
      onAdded();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="flex flex-wrap gap-2 pt-3">
      <Input placeholder="e.g. S6 or Chemical store" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="h-9 w-56" data-testid="room-new-name" />
      <Select value={f.kind} onValueChange={(v) => setF({ ...f, kind: v })}>
        <SelectTrigger className="h-9 w-36" data-testid="room-new-kind"><SelectValue /></SelectTrigger>
        <SelectContent><SelectItem value="lab">Lab</SelectItem><SelectItem value="storage">Storage area</SelectItem></SelectContent>
      </Select>
      <Button size="sm" className="h-9" disabled={!f.name.trim()} onClick={add} data-testid="room-add-button"><Plus className="h-4 w-4 mr-1" />Add</Button>
    </div>
  );
}

export function RoomsManager() {
  const [rooms, setRooms] = useState(null);
  const [techs, setTechs] = useState([]);
  const load = useCallback(() => api.get("/rooms").then((r) => setRooms(r.data)), []);
  useEffect(() => {
    load();
    api.get("/timetable/tech-setup").then((r) => setTechs(r.data.technicians)).catch(() => {});
  }, [load]);
  if (!rooms) return <p className="text-sm text-muted-foreground">Loading…</p>;
  const group = (kind, Icon, hint) => (
    <section className="rounded-xl border bg-card p-4" data-testid={`rooms-section-${kind}`}>
      <h3 className="font-semibold flex items-center gap-2"><Icon className="h-4 w-4" />{KIND_LABEL[kind]}s</h3>
      <p className="text-xs text-muted-foreground mb-2">{hint}</p>
      {rooms.filter((r) => r.kind === kind).map((r) => <RoomRow key={`${r.id}-${r.name}-${(r.aliases || []).join()}`} r={r} techs={techs} onChanged={load} />)}
      {!rooms.some((r) => r.kind === kind) && <p className="text-sm text-muted-foreground py-2">None yet.</p>}
    </section>
  );
  return (
    <div className="space-y-4" data-testid="rooms-manager">
      {group("lab", FlaskConical, "Used in timetables. Uploaded timetables match labs by name, ignoring spaces, capitals and words like 'Lab' or 'Room' (so 'Lab S1' = 'S1'); add other names a timetable uses below each lab. The technician set here preps requests for lessons in that lab.")}
      {group("storage", Warehouse, "Where stock is kept. These appear in the Inventory storage location list.")}
      <AddRoom onAdded={load} />
    </div>
  );
}
